import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Alert, Card, Col, Row } from "antd";
import moment from "moment";

import LoadingPage from "../../components/LoadingPage";
import Error404Page from "../../components/Error404Page";

import {
  CUSTOMER_MAINTENANCE_WRIKE_ID,
  CUSTOMER_SUPPORT_WRIKE_ID,
  WRIKE_TASK_PARAMS,
  CARD_CONFIG_STYLE,
} from "../../libs/constants";

import {
  getTickets,
  getCustomStatuses,
  getUpcomingEvents,
} from "../wrike/wrikeSlice";
import Wrike from "../wrike/Wrike";

import RobotInfo from "./RobotInfo";
import RobotDetail from "./RobotDetail";
import RobotScheduledEvents from "./RobotScheduledEvents";
import RobotAlarmStatistics from "../alarms/RobotAlarmStatistics";
import RobotPerformance from "./RobotPerformance";

import { getRobotPLCAlarms } from "../alarms/alarmSlice";
import { getRobotWeld, getRobotTier } from "../robot/robotSlice";

const RobotSummary = (props) => {
  const { robotName } = useParams();
  const robotId =
    robotName && robotName.match(/(\d+)/) && robotName.match(/(\d+)/)[0];

  const robots = useSelector((state) => state.robot.robots);
  const tickets = useSelector((state) => state.wrike.tasks);
  const events = useSelector((state) => state.wrike.events);
  const alarms = useSelector((state) => state.alarms.alarms);
  const weldRun = useSelector((state) => state.robot.weldRun);
  const tier = useSelector((state) => state.robot.tier);

  const lastNDays = {
    key: 1,
    title: "Last 24 hours",
    value: 1,
    date: moment().subtract(1, "days"),
  };

  const { isCustomer } = props;

  const dispatch = useDispatch();
  useEffect(() => {
    if (!isCustomer) {
      dispatch(
        getTickets(
          `folders/${CUSTOMER_SUPPORT_WRIKE_ID}/tasks`,
          WRIKE_TASK_PARAMS
        )
      );
      dispatch(
        getUpcomingEvents(
          `folders/${CUSTOMER_MAINTENANCE_WRIKE_ID}/tasks`,
          WRIKE_TASK_PARAMS
        )
      );

      dispatch(getCustomStatuses());
    }
    dispatch(getRobotPLCAlarms(robotId));
    dispatch(getRobotWeld(robotId));
    dispatch(getRobotTier(robotId));
  }, [dispatch, robotId]);

  if (!robotId) {
    return <Error404Page />;
  }

  if (!robots) {
    return <LoadingPage />;
  }
  if (!alarms) {
    return <LoadingPage />;
  }

  if (!weldRun) {
    return <LoadingPage />;
  }

  if (!tier) {
    return <LoadingPage />;
  }

  const robot = robots.find((item) => item.name === robotName);

  if (!robot) {
    return <Error404Page />;
  }

  const parseAlarms = (plcAlarms, systemAlarms) => {
    return [
      ...systemAlarms
        .map((item, index) => {
          if (robotName === item.name && item.summary && item.summary.data) {
            const coreFiles =
              parseInt(item.summary.data[0].core_files, 10) || null;
            if (coreFiles > 0) {
              return {
                key: "sys" + index,
                unit: item.name,
                message: `System crashed ${coreFiles} times`,
                type: "crash",
                received_at: item.summary.date,
                detail: "Please contact customer to investigate.",
              };
            } else {
              return null;
            }
          } else {
            return null;
          }
        })
        .filter((item) => item),

      ...systemAlarms
        .map((item, index) => {
          if (robotName === item.name && item.space) {
            const usedSpace = item.space
              ? (parseInt(item.space.used, 10) /
                  parseInt(item.space.total, 10)) *
                100
              : null;
            if (usedSpace > 75) {
              return {
                key: "ipc" + index,
                unit: item.name,
                message: `${item.space.free} disk space left`,
                type: usedSpace > 90 ? "error" : "warning",
                received_at: item.summary.date,
                detail: "Please contact customer to free up the space.",
              };
            } else {
              return null;
            }
          } else {
            return null;
          }
        })
        .filter((item) => item),

      ...plcAlarms.map((item, index) => {
        return { key: "plc" + index, ...item };
      }),
    ];
  };

  const data = parseAlarms(alarms, robots);

  const statsData = {
    warning:
      data.filter(
        (item) =>
          moment(item.received_at).isSameOrAfter(
            lastNDays.date.format("YYYY-MM-DD")
          ) && item.type === "warning"
      ).length || 0,
    error:
      data.filter(
        (item) =>
          moment(item.received_at).isSameOrAfter(
            lastNDays.date.format("YYYY-MM-DD")
          ) && item.type === "error"
      ).length || 0,
    crash:
      data.filter(
        (item) =>
          moment(item.received_at).isSameOrAfter(
            lastNDays.date.format("YYYY-MM-DD")
          ) && item.type === "crash"
      ).length || 0,

    weld_done:
      (weldRun[robotId] &&
        weldRun[robotId].length > 0 &&
        weldRun[robotId].filter((item) =>
          moment(item.arc_on_utc).isSameOrAfter(
            lastNDays.date.format("YYYY-MM-DD")
          )
        ).length) ||
      0,
  };

  return (
    <>
      <Alert
        style={{ marginBottom: 10 }}
        message={`${
          statsData.error + statsData.crash + statsData.warning > 0
            ? `SWR status: ${statsData.error + statsData.crash || 0} errors, ${
                statsData.warning || 0
              } warnings reported ${
                lastNDays.value === 1
                  ? "since yesterday"
                  : "in last " + lastNDays.value + " days"
              }`
            : "SWR status: OK"
        }`}
        description={`${statsData.weld_done} jobs done ${
          lastNDays.value === 1
            ? "since yesterday"
            : "in last " + lastNDays.value + " days"
        }`}
        showIcon
        type={
          statsData.crash + statsData.error > 0
            ? "error"
            : statsData.warning > 0
            ? "warning"
            : "success"
        }
        banner
        closable
      />

      <Row gutter={[4, 4]}>
        <Col span={isCustomer ? 12 : 8}>
          <Card
            {...CARD_CONFIG_STYLE}
            title="Unit Summary"
            style={{ height: "100%" }}
          >
            <RobotInfo
              robot={robot}
              isCustomer={isCustomer}
              tier={tier[robotId]}
            />
          </Card>
        </Col>
        <Col span={isCustomer ? 12 : 8}>
          <Card
            {...CARD_CONFIG_STYLE}
            title={!isCustomer ? "Activity / Health" : "Health"}
            style={{ height: "100%" }}
          >
            <RobotAlarmStatistics
              data={data}
              weldRun={weldRun[robotId]}
              isCustomer={isCustomer}
            />
          </Card>
        </Col>

        {!isCustomer && (
          <>
            <Col span={isCustomer ? 12 : 8}>
              <Card
                {...CARD_CONFIG_STYLE}
                title="Performance"
                style={{ height: "100%" }}
              >
                <RobotPerformance
                  robot={robot}
                  weldRun={weldRun[robotId]}
                  tier={tier[robotId]}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card
                {...CARD_CONFIG_STYLE}
                title="Detail Information"
                style={{ height: "100%" }}
              >
                <RobotDetail robot={robot} />
              </Card>
            </Col>

            <Col span={8}>
              <Card
                {...CARD_CONFIG_STYLE}
                title="Wrike Tasks"
                style={{ height: "100%" }}
              >
                <Wrike tasks={tickets} style={{ height: 282 }} />
              </Card>
            </Col>
            <Col span={8}>
              <Card
                {...CARD_CONFIG_STYLE}
                title="Preventative Maintenance Checklist"
                style={{ height: "100%" }}
              >
                <RobotScheduledEvents
                  events={events}
                  style={{ height: 272, overflowY: "scroll", paddingTop: 10 }}
                />
              </Card>
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

export default RobotSummary;
